import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, Save, Shield, UserCog, Plus, X } from "lucide-react";
import Seo from "@/components/Seo";
import BulkUsersToolbar from "@/components/admin/BulkUsersToolbar";
import UserRow from "@/components/admin/UserRow";
import { useBulkUserActions } from "@/hooks/useBulkUserActions";

function PasswordField({ value, onChange, placeholder, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="input-tcpi pr-12"
        data-testid={testid}
        autoComplete="off"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8782] hover:text-[#C9A961]"
        aria-label={show ? "Hide password" : "Show password"}
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

function ChangePasswordCard() {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 12) return toast.error("New password must be at least 12 characters.");
    if (newPw !== confirm) return toast.error("New password and confirmation don't match.");
    setBusy(true);
    try {
      await api.post("/me/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Password changed. Use it next time you sign in.");
      setOldPw(""); setNewPw(""); setConfirm("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not change password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card-bento space-y-4" data-testid="change-password-card">
      <div className="flex items-center gap-3">
        <KeyRound className="w-5 h-5 text-[#C9A961]" />
        <div>
          <div className="overline">Change password</div>
          <p className="text-xs text-[#8A8782] mt-1">Minimum 12 characters. Mix in letters, numbers, and a symbol.</p>
        </div>
      </div>
      <PasswordField value={oldPw} onChange={setOldPw} placeholder="Current password" testid="cp-old" />
      <PasswordField value={newPw} onChange={setNewPw} placeholder="New password (≥12 chars)" testid="cp-new" />
      <PasswordField value={confirm} onChange={setConfirm} placeholder="Confirm new password" testid="cp-confirm" />
      <button
        type="submit"
        disabled={busy || !oldPw || !newPw}
        className="btn-primary flex items-center gap-2 disabled:opacity-50"
        data-testid="cp-submit"
      >
        <Save className="w-4 h-4" /> {busy ? "Saving…" : "Update password"}
      </button>
    </form>
  );
}

function CreateUserModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    email: "", password: "", name: "",
    role: "facilitator", program: "men", organization: "", pin: "",
  });
  const [busy, setBusy] = useState(false);
  const [needsPin, setNeedsPin] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (open) {
      setForm({ email: "", password: "", name: "", role: "facilitator", program: "men", organization: "", pin: "" });
      setNeedsPin(false);
      setErr("");
    }
  }, [open]);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const payload = { ...form };
      if (!payload.pin) delete payload.pin;
      const { data } = await api.post("/admin/users", payload);
      toast.success(`Created ${data.email}`);
      onCreated(data);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Could not create user.";
      if (err.response?.status === 409 && /pin/i.test(msg)) {
        setNeedsPin(true);
        setErr(msg);
      } else {
        toast.error(msg);
        setErr(msg);
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#0A0908]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="card-bento w-full max-w-md space-y-3"
        data-testid="create-user-modal"
      >
        <div className="flex items-center justify-between">
          <div className="overline">Create user</div>
          <button type="button" onClick={onClose} className="text-[#8A8782] hover:text-[#C9A961]"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-[11px] text-[#8A8782] italic">
          To add a second user to an email already in use (a cohort sharing one inbox), set
          a 4-digit PIN that the new user will use at sign-in.
        </p>
        <input className="input-tcpi" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cu-email" />
        <input className="input-tcpi" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cu-name" />
        <PasswordField value={form.password} onChange={(v) => setForm({ ...form, password: v })} placeholder="Initial password (≥8)" testid="cu-password" />
        <div className="grid grid-cols-2 gap-3">
          <select className="input-tcpi" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="cu-role">
            <option value="participant">Participant</option>
            <option value="facilitator">Facilitator</option>
            <option value="admin">Admin</option>
          </select>
          <select className="input-tcpi" value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value })} data-testid="cu-program">
            <option value="men">Men's program</option>
            <option value="women">Women's program</option>
          </select>
        </div>
        <input className="input-tcpi" placeholder="Organization (optional)" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} />

        {needsPin && (
          <div data-testid="cu-pin-wrap">
            <label className="block text-xs text-[#C9A961] mb-1.5 uppercase tracking-wider">4-digit PIN for shared email</label>
            <input
              className="input-tcpi tracking-[0.4em] text-center"
              inputMode="numeric"
              maxLength={4}
              pattern="\d{4}"
              value={form.pin}
              onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })}
              placeholder="• • • •"
              data-testid="cu-pin"
            />
          </div>
        )}

        {err && <div className="text-xs text-red-400" data-testid="cu-error">{err}</div>}

        <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-50" data-testid="cu-submit">
          {busy ? "Creating…" : needsPin ? "Create with PIN" : "Create user"}
        </button>
      </form>
    </div>
  );
}

function ManageUsersCard() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch {
      toast.error("Could not load users.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Bulk selection helpers — never include yourself; backend would reject anyway.
  const toggleOne = (id) => {
    if (id === me?.id) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const bulk = useBulkUserActions({ selectedIds, clearSelection, reload: load });

  const handleApiAction = async (label, action) => {
    try {
      await action();
      toast.success(label);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Update failed.");
    }
  };

  const toggleActive = (u) => {
    const currentlyActive = u.is_active !== false;
    return handleApiAction(
      `${u.email} ${currentlyActive ? "deactivated" : "reactivated"}`,
      () => api.patch(`/admin/users/${u.id}`, { is_active: !currentlyActive }),
    );
  };
  const updateRole = (u, nextRole) => {
    if (nextRole === u.role) return;
    const warning = nextRole === "admin"
      ? "\n\nWARNING: admin accounts can manage every user and delete data." : "";
    if (!window.confirm(`Change ${u.email}'s role from "${u.role}" to "${nextRole}"?${warning}`)) return;
    return handleApiAction(`${u.email} is now ${nextRole}`,
      () => api.patch(`/admin/users/${u.id}`, { role: nextRole }));
  };
  const updateProgram = (u, nextProgram) => {
    if (nextProgram === u.program) return;
    return handleApiAction(`${u.email} moved to ${nextProgram === "men" ? "Men's" : "Women's"} program`,
      () => api.patch(`/admin/users/${u.id}`, { program: nextProgram }));
  };
  const remove = (u) => {
    if (!window.confirm(`Delete ${u.email}? This will also remove their journals, assessments, posts, and progress. This cannot be undone.`)) return;
    return handleApiAction(`Deleted ${u.email}`, () => api.delete(`/admin/users/${u.id}`));
  };

  const visible = users.filter((u) =>
    !filter || u.email?.toLowerCase().includes(filter.toLowerCase()) || u.name?.toLowerCase().includes(filter.toLowerCase())
  );

  // Selectable = every visible user EXCEPT yourself.
  const selectableVisible = useMemo(
    () => visible.filter((u) => u.id !== me?.id),
    [visible, me?.id],
  );
  const allVisibleSelected =
    selectableVisible.length > 0 &&
    selectableVisible.every((u) => selectedIds.has(u.id));
  const someVisibleSelected =
    selectableVisible.some((u) => selectedIds.has(u.id)) && !allVisibleSelected;

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const u of selectableVisible) next.delete(u.id);
      } else {
        for (const u of selectableVisible) next.add(u.id);
      }
      return next;
    });
  };

  return (
    <div className="card-bento" data-testid="manage-users-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <UserCog className="w-5 h-5 text-[#C9A961]" />
          <div>
            <div className="overline">Manage users</div>
            <p className="text-xs text-[#8A8782] mt-1">{users.length} accounts · admin only</p>
          </div>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="btn-primary text-xs flex items-center gap-2"
          data-testid="manage-users-create-btn"
        >
          <Plus className="w-3.5 h-3.5" /> New user
        </button>
      </div>

      <input
        className="input-tcpi mb-4"
        placeholder="Filter by email or name…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        data-testid="manage-users-filter"
      />

      {selectedIds.size > 0 && (
        <BulkUsersToolbar
          selectedCount={selectedIds.size}
          bulkBusy={bulk.bulkBusy}
          onReactivate={bulk.bulkReactivate}
          onDeactivate={bulk.bulkDeactivate}
          onSetRole={bulk.bulkSetRole}
          onSetProgram={bulk.bulkSetProgram}
          onDelete={bulk.bulkDelete}
          onClear={clearSelection}
        />
      )}

      {loading ? (
        <p className="text-sm text-[#8A8782]">Loading…</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="manage-users-table">
            <thead>
              <tr className="text-left text-[#8A8782] text-xs uppercase tracking-wider border-b border-[#C9A961]/15">
                <th className="py-2 pr-3 w-8">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
                    onChange={toggleSelectAllVisible}
                    disabled={selectableVisible.length === 0}
                    className="accent-[#C9A961] cursor-pointer"
                    aria-label="Select all visible users"
                    data-testid="select-all-visible"
                  />
                </th>
                <th className="py-2 pr-3">Email</th>
                <th className="py-2 pr-3 hidden sm:table-cell">Name</th>
                <th className="py-2 pr-3">Role</th>
                <th className="py-2 pr-3 hidden md:table-cell">Program</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  isSelf={u.id === me?.id}
                  selected={selectedIds.has(u.id)}
                  onToggleSelect={toggleOne}
                  onRoleChange={updateRole}
                  onProgramChange={updateProgram}
                  onToggleActive={toggleActive}
                  onDelete={remove}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateUserModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={load} />
    </div>
  );
}

export default function AccountSecurity() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  return (
    <div className="bg-tcpi-bg" data-testid="account-security-page">
      <Seo title="Account security" path="/account/security" noindex />
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-6 h-6 text-[#C9A961]" />
          <h1
            className="text-3xl md:text-4xl text-[#F7F5F0]"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Account & Security
          </h1>
        </div>
        <p className="text-[#8A8782] mb-10">Signed in as <span className="text-[#C9A961]">{user?.email}</span> · role <span className="text-[#C9A961]">{user?.role}</span></p>

        <div className="space-y-8">
          <ChangePasswordCard />
          {isAdmin && <ManageUsersCard />}
        </div>
      </div>
    </div>
  );
}
